import React, { useState, useEffect, useCallback } from 'react';
import { API_ENDPOINTS } from '../../config/apiRouting';
import authenticatedFetch from '../../utils/authenticatedFetch';
import './SocialMediaCalendar.css';

interface Post {
  postId: string;
  title: string;
  content: string;
  mediaUrls: string[];
  hashtags: string[];
  scheduledAt: string;
  timezone: string;
  status: 'draft' | 'scheduled' | 'published' | 'failed';
  platforms: string[];
  isRecurring: boolean;
  recurringPattern?: {
    type: 'daily' | 'weekly' | 'monthly';
    interval: number;
    dayOfWeek?: number;
    dayOfMonth?: number;
    time: string;
    endDate?: string;
  };
  campaignId?: string;
  discountId?: string;
  offerCode?: string;
  isBoosted: boolean;
  boostBudget?: number;
}

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  post: Post;
  color: string;
  isRecurring: boolean;
}

interface Store {
  storeId: string;
  name: string;
  timezone: string;
  address: {
    city: string;
    state: string;
  };
}

const PLATFORM_COLORS = {
  facebook: '#1877f2',
  instagram: '#E4405F',
  twitter: '#1DA1F2',
  linkedin: '#0077b5',
  tiktok: '#000000'
};

const STATUS_COLORS = {
  draft: '#6c757d',
  scheduled: '#ffc107',
  published: '#28a745',
  failed: '#dc3545'
};

export const SocialMediaCalendar: React.FC = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [selectedStore, setSelectedStore] = useState<string>('');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'month' | 'week' | 'day'>('month');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPostModal, setShowPostModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [draggedEvent, setDraggedEvent] = useState<CalendarEvent | null>(null);

  const [newPost, setNewPost] = useState<Partial<Post>>({
    title: '',
    content: '',
    hashtags: [],
    platforms: [],
    scheduledAt: '',
    timezone: 'America/New_York',
    isRecurring: false,
    isBoosted: false
  });

  useEffect(() => {
    fetchStores();
  }, []);

  useEffect(() => {
    if (selectedStore) {
      fetchPosts();
    }
  }, [selectedStore]);

  useEffect(() => {
    generateCalendarEvents();
  }, [posts]);

  const fetchStores = async () => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.stores());
      if (!response.ok) throw new Error('Failed to fetch stores');
      const data = await response.json();
      const storeList = Array.isArray(data) ? data : (data.data || data.stores || []);
      setStores(storeList);
      if (storeList.length > 0) {
        setSelectedStore(storeList[0].storeId);
        setNewPost(prev => ({ ...prev, timezone: storeList[0].timezone || 'America/New_York' }));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch stores');
    }
  };

  const fetchPosts = async () => {
    if (!selectedStore) return;

    setLoading(true);
    try {
      const response = await authenticatedFetch(`${API_ENDPOINTS.socialMediaPosts()}?storeId=${selectedStore}`);
      if (!response.ok) throw new Error('Failed to fetch posts');
      const data = await response.json();
      setPosts(data.data || []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch posts');
    } finally {
      setLoading(false);
    }
  };

  const generateCalendarEvents = useCallback(() => {
    const calendarEvents: CalendarEvent[] = [];
    
    posts.forEach(post => {
      const startDate = new Date(post.scheduledAt);
      const endDate = new Date(startDate);
      endDate.setHours(endDate.getHours() + 1); // 1 hour duration
      
      // Primary event
      const primaryColor = post.platforms.length > 0 
        ? PLATFORM_COLORS[post.platforms[0] as keyof typeof PLATFORM_COLORS] 
        : STATUS_COLORS[post.status];
        
      calendarEvents.push({
        id: post.postId,
        title: post.title || 'Social Media Post',
        start: startDate,
        end: endDate,
        post,
        color: primaryColor,
        isRecurring: post.isRecurring
      });
      
      // Generate recurring events
      if (post.isRecurring && post.recurringPattern) {
        const pattern = post.recurringPattern;
        const endDate = pattern.endDate ? new Date(pattern.endDate) : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000); // 1 year default
        
        let nextDate = new Date(startDate);
        let count = 0;
        const maxOccurrences = 52; // Limit to prevent infinite generation
        
        while (nextDate <= endDate && count < maxOccurrences) {
          switch (pattern.type) {
            case 'daily':
              nextDate.setDate(nextDate.getDate() + pattern.interval);
              break;
            case 'weekly':
              nextDate.setDate(nextDate.getDate() + (7 * pattern.interval));
              break;
            case 'monthly':
              nextDate.setMonth(nextDate.getMonth() + pattern.interval);
              break;
          }
          
          if (nextDate <= endDate) {
            const recurringEnd = new Date(nextDate);
            recurringEnd.setHours(recurringEnd.getHours() + 1);
            
            calendarEvents.push({
              id: `${post.postId}-recurring-${count}`,
              title: `${post.title || 'Recurring Post'} (Auto)`,
              start: new Date(nextDate),
              end: recurringEnd,
              post: { ...post, scheduledAt: nextDate.toISOString() },
              color: primaryColor,
              isRecurring: true
            });
          }
          count++;
        }
      }
    });
    
    setEvents(calendarEvents);
  }, [posts]);

  const handleDateClick = (date: Date) => {
    const localDate = new Date(date);
    localDate.setMinutes(localDate.getMinutes() - localDate.getTimezoneOffset());
    
    setNewPost({
      title: '',
      content: '',
      hashtags: [],
      platforms: [],
      scheduledAt: localDate.toISOString().slice(0, 16),
      timezone: stores.find(s => s.storeId === selectedStore)?.timezone || 'America/New_York',
      isRecurring: false,
      isBoosted: false
    });
    setSelectedEvent(null);
    setShowPostModal(true);
  };

  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setNewPost(event.post);
    setShowPostModal(true);
  };

  const handleSavePost = async () => {
    if (!selectedStore) return;

    try {
      const method = selectedEvent ? 'PUT' : 'POST';
      const url = selectedEvent
        ? API_ENDPOINTS.socialMediaPost(selectedEvent.post.postId)
        : API_ENDPOINTS.socialMediaPosts();

      const response = await authenticatedFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newPost,
          storeId: selectedStore
        })
      });

      if (!response.ok) throw new Error('Failed to save post');
      
      await fetchPosts();
      setShowPostModal(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save post');
    }
  };

  const handleDeletePost = async () => {
    if (!selectedEvent) return;
    
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      const response = await authenticatedFetch(API_ENDPOINTS.socialMediaPost(selectedEvent.post.postId), {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Failed to delete post');
      
      await fetchPosts();
      setShowPostModal(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete post');
    }
  };

  const formatTimeForTimezone = (date: Date, timezone: string) => {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).format(date);
  };

  const renderCalendarGrid = () => {
    const today = new Date();
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    // Get first day of month and last day of month
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // Get first day of calendar (might be from previous month)
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    // Generate 42 days (6 weeks)
    const days = [];
    for (let i = 0; i < 42; i++) {
      const day = new Date(startDate);
      day.setDate(startDate.getDate() + i);
      days.push(day);
    }

    return (
      <div className="calendar-grid">
        <div className="calendar-header">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="day-header">{day}</div>
          ))}
        </div>
        <div className="calendar-body">
          {days.map((day, index) => {
            const dayEvents = events.filter(event => {
              const eventDate = new Date(event.start);
              return eventDate.toDateString() === day.toDateString();
            });

            const isToday = day.toDateString() === today.toDateString();
            const isCurrentMonth = day.getMonth() === month;
            const isWeekend = day.getDay() === 0 || day.getDay() === 6;

            return (
              <div
                key={index}
                className={`calendar-day ${isToday ? 'today' : ''} ${!isCurrentMonth ? 'other-month' : ''} ${isWeekend ? 'weekend' : ''}`}
                onClick={() => handleDateClick(day)}
              >
                <div className="day-number">{day.getDate()}</div>
                <div className="day-events">
                  {dayEvents.slice(0, 3).map(event => (
                    <div
                      key={event.id}
                      className="calendar-event"
                      style={{ backgroundColor: event.color }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEventClick(event);
                      }}
                      title={`${event.title}\n${formatTimeForTimezone(event.start, newPost.timezone || 'America/New_York')}\nPlatforms: ${event.post.platforms.join(', ')}`}
                    >
                      <span className="event-title">{event.title}</span>
                      <span className="event-time">
                        {formatTimeForTimezone(event.start, event.post.timezone)}
                      </span>
                      {event.isRecurring && <span className="recurring-indicator">[RECURRING]</span>}
                    </div>
                  ))}
                  {dayEvents.length > 3 && (
                    <div className="more-events">+{dayEvents.length - 3} more</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  if (loading && !posts.length) {
    return <div className="calendar-loading">Loading calendar...</div>;
  }

  return (
    <div className="social-media-calendar">
      <div className="calendar-header-controls">
        <h1>Social Media Calendar</h1>
        
        <div className="header-controls">
          <div className="store-selector">
            <select 
              value={selectedStore} 
              onChange={(e) => setSelectedStore(e.target.value)}
              className="store-dropdown"
            >
              {stores.map(store => (
                <option key={store.storeId} value={store.storeId}>
                  {store.name} - {store.address?.city}
                </option>
              ))}
            </select>
          </div>

          <div className="view-controls">
            <button
              className={`view-btn ${viewMode === 'month' ? 'active' : ''}`}
              onClick={() => setViewMode('month')}
            >
              Month
            </button>
            <button
              className={`view-btn ${viewMode === 'week' ? 'active' : ''}`}
              onClick={() => setViewMode('week')}
            >
              Week
            </button>
            <button
              className={`view-btn ${viewMode === 'day' ? 'active' : ''}`}
              onClick={() => setViewMode('day')}
            >
              Day
            </button>
          </div>

          <button 
            className="create-post-btn"
            onClick={() => handleDateClick(new Date())}
          >
            + New Post
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <span>[ERROR] {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="calendar-navigation">
        <button onClick={() => navigateMonth('prev')}>←</button>
        <h2>
          {currentDate.toLocaleDateString('en-US', { 
            month: 'long', 
            year: 'numeric' 
          })}
        </h2>
        <button onClick={() => navigateMonth('next')}>→</button>
        
        <div className="timezone-info">
          {stores.find(s => s.storeId === selectedStore)?.timezone && (
            <span>
              [TIME] {stores.find(s => s.storeId === selectedStore)?.timezone}
            </span>
          )}
        </div>
      </div>

      {viewMode === 'month' && renderCalendarGrid()}

      <div className="calendar-legend">
        <h3>Platform Legend</h3>
        <div className="legend-items">
          {Object.entries(PLATFORM_COLORS).map(([platform, color]) => (
            <div key={platform} className="legend-item">
              <div className="legend-color" style={{ backgroundColor: color }}></div>
              <span>{platform.charAt(0).toUpperCase() + platform.slice(1)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Post Creation/Edit Modal */}
      {showPostModal && (
        <div className="modal-overlay">
          <div className="post-modal">
            <div className="modal-header">
              <h2>{selectedEvent ? 'Edit Post' : 'Create New Post'}</h2>
              <button onClick={() => setShowPostModal(false)}>×</button>
            </div>

            <div className="modal-content">
              <div className="form-group">
                <label htmlFor="post-title">Post Title</label>
                <input
                  id="post-title"
                  type="text"
                  value={newPost.title || ''}
                  onChange={(e) => setNewPost({...newPost, title: e.target.value})}
                  placeholder="Enter post title..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="post-content">Content</label>
                <textarea
                  id="post-content"
                  value={newPost.content || ''}
                  onChange={(e) => setNewPost({...newPost, content: e.target.value})}
                  placeholder="What would you like to share..."
                  rows={4}
                />
              </div>

              <div className="form-group">
                <label htmlFor="post-hashtags">Hashtags (comma-separated)</label>
                <input
                  id="post-hashtags"
                  type="text"
                  value={newPost.hashtags?.join(', ') || ''}
                  onChange={(e) => setNewPost({
                    ...newPost, 
                    hashtags: e.target.value.split(',').map(tag => tag.trim()).filter(Boolean)
                  })}
                  placeholder="#restaurant, #food, #special"
                />
              </div>

              <div className="form-group">
                <label>Platforms</label>
                <div className="platform-checkboxes">
                  {['facebook', 'instagram', 'twitter', 'linkedin', 'tiktok'].map(platform => (
                    <label key={platform} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={newPost.platforms?.includes(platform) || false}
                        onChange={(e) => {
                          const platforms = newPost.platforms || [];
                          if (e.target.checked) {
                            setNewPost({...newPost, platforms: [...platforms, platform]});
                          } else {
                            setNewPost({...newPost, platforms: platforms.filter(p => p !== platform)});
                          }
                        }}
                      />
                      <span style={{ color: PLATFORM_COLORS[platform as keyof typeof PLATFORM_COLORS] }}>
                        {platform.charAt(0).toUpperCase() + platform.slice(1)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="scheduled-date">Scheduled Date & Time</label>
                  <input
                    id="scheduled-date"
                    type="datetime-local"
                    value={newPost.scheduledAt || ''}
                    onChange={(e) => setNewPost({...newPost, scheduledAt: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="timezone">Timezone</label>
                  <select
                    id="timezone"
                    value={newPost.timezone || 'America/New_York'}
                    onChange={(e) => setNewPost({...newPost, timezone: e.target.value})}
                  >
                    <option value="America/New_York">Eastern Time</option>
                    <option value="America/Chicago">Central Time</option>
                    <option value="America/Denver">Mountain Time</option>
                    <option value="America/Los_Angeles">Pacific Time</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={newPost.isRecurring || false}
                    onChange={(e) => setNewPost({...newPost, isRecurring: e.target.checked})}
                  />
                  Recurring Post
                </label>
              </div>

              {newPost.isRecurring && (
                <div className="recurring-options">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Repeat</label>
                      <select
                        value={newPost.recurringPattern?.type || 'weekly'}
                        onChange={(e) => setNewPost({
                          ...newPost,
                          recurringPattern: {
                            ...newPost.recurringPattern,
                            type: e.target.value as 'daily' | 'weekly' | 'monthly',
                            interval: 1,
                            time: '12:00'
                          }
                        })}
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Every</label>
                      <input
                        type="number"
                        min={1}
                        max={52}
                        value={newPost.recurringPattern?.interval || 1}
                        onChange={(e) => setNewPost({
                          ...newPost,
                          recurringPattern: {
                            ...newPost.recurringPattern!,
                            interval: parseInt(e.target.value)
                          }
                        })}
                      />
                      <span className="interval-label">
                        {newPost.recurringPattern?.type === 'daily' ? 'day(s)' : 
                         newPost.recurringPattern?.type === 'weekly' ? 'week(s)' : 'month(s)'}
                      </span>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>End Date (optional)</label>
                    <input
                      type="date"
                      value={newPost.recurringPattern?.endDate || ''}
                      onChange={(e) => setNewPost({
                        ...newPost,
                        recurringPattern: {
                          ...newPost.recurringPattern!,
                          endDate: e.target.value
                        }
                      })}
                    />
                  </div>
                </div>
              )}

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={newPost.isBoosted || false}
                    onChange={(e) => setNewPost({...newPost, isBoosted: e.target.checked})}
                  />
                  Boost Post
                </label>
              </div>

              {newPost.isBoosted && (
                <div className="form-group">
                  <label htmlFor="boost-budget">Boost Budget ($)</label>
                  <input
                    id="boost-budget"
                    type="number"
                    min={5}
                    step={5}
                    value={newPost.boostBudget || ''}
                    onChange={(e) => setNewPost({...newPost, boostBudget: parseFloat(e.target.value)})}
                    placeholder="50"
                  />
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button onClick={() => setShowPostModal(false)}>Cancel</button>
              {selectedEvent && (
                <button onClick={handleDeletePost} className="delete-btn">
                  Delete
                </button>
              )}
              <button 
                onClick={handleSavePost}
                className="save-btn"
                disabled={!newPost.title || !newPost.content || !newPost.scheduledAt || !newPost.platforms?.length}
              >
                {selectedEvent ? 'Update' : 'Schedule'} Post
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};